import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-10970c49"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "relative flex items-center mb-4 md:items-stretch"
}
const _hoisted_2 = ["disabled"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, "Previous", -1))
const _hoisted_4 = ["disabled"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, "Next", -1))
const _hoisted_6 = { class: "text-sm font-semibold text-gray-900" }
const _hoisted_7 = { class: "mt-2 grid grid-cols-7 text-xs cursor-default leading-6 text-gray-500 z-10" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { key: 0 }
const _hoisted_10 = {
  key: 0,
  class: "topLeftTriangle"
}
const _hoisted_11 = {
  key: 1,
  class: "bottomLeftTriangle"
}
const _hoisted_12 = ["onMouseover"]
const _hoisted_13 = { class: "text-sm" }
const _hoisted_14 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", { class: "w-full" }, null, -1))
const _hoisted_15 = {
  key: 0,
  class: "flex items-center gap-0.5"
}
const _hoisted_16 = { key: 1 }
const _hoisted_17 = { class: "capitalize truncate col-span-1" }
const _hoisted_18 = {
  class: "rounded px-1 text-white py-0.5 mt-1",
  style: { backgroundColor: '#bfdbfe4c', color: '#1d4ed8' }
}
const _hoisted_19 = ["title"]
const _hoisted_20 = { class: "bg-yellow-300 text-yellow-700 px-1.5 py-0.5 rounded" }
const _hoisted_21 = { key: 2 }
const _hoisted_22 = { class: "bg-gray-400 text-white px-1.5 py-0.5 rounded" }
const _hoisted_23 = {
  key: 0,
  class: "flex items-center gap-1"
}
const _hoisted_24 = { class: "capitalize" }
const _hoisted_25 = {
  key: 0,
  class: "flex items-center gap-1"
}
const _hoisted_26 = { class: "capitalize" }
const _hoisted_27 = {
  key: 1,
  class: "flex justify-center items-center w-full h-full"
}

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { computed, Ref, ref } from "vue";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import { useIsWorkingDay, useNonWorkingDaysByDaySet } from "shared/composables/durations";
import { useProjectDurationSettings } from "shared/composables/projectDurationSettings";
import { ShortenedProcess } from "shared/types/Process";
import { ProcessClass, ProcessElement } from "shared/types/ProcessClass";
import { useOutagesByRange } from "@/composables/outages";
import { useProcessClasses } from "@/composables/process";
import { useStreams } from "@/composables/stream";
import { processElementColor } from "@/constants/processClasses";
import { workingDaysColorCodes } from "@/constants/workingDays";
import router from "@/router";
import processDurationService from "@/services/processDurationService";

const itemsPerPage = 8;


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkingDaysCalendar',
  props: {
    processes: {},
    interval: {}
  },
  setup(__props: any) {

const processClasses = useProcessClasses();

const props = __props;

const calendar = ref(null);
const { streams } = useStreams();
const { projectDurationSettings } = useProjectDurationSettings();
const nonWorkingDaysByDaySet = useNonWorkingDaysByDaySet();
const isWorkingDay = useIsWorkingDay();

const intervalOrFallback: Ref<[Date, Date]> = computed(() => {
  const firstProcessDate = processDurationService.getFirstProcessDate(props.processes);
  const lastProcessDate = processDurationService.getLastProcessDate(props.processes);
  return props.interval || [firstProcessDate, lastProcessDate];
});

const outagesInterval = computed(() => ({
  start: intervalOrFallback.value[0],
  end: intervalOrFallback.value[1],
}));

const tooltipDay = ref();

const { outagesByRange, isLoading } = useOutagesByRange(outagesInterval);
const intervalDateStrings = computed(
  () =>
    new Set<string>(
      eachDayOfInterval({
        start: intervalOrFallback.value[0],
        end: intervalOrFallback.value[1],
      }).map((date) => format(date, "yyyy-MM-dd")),
    ),
);

const visibleMonthsStartDate = computed(() =>
  eachMonthOfInterval({
    start: intervalOrFallback.value[0],
    end: intervalOrFallback.value[1],
  }),
);

const getProcessClassFromEncodedLabel = processClasses.value.reduce((acc, processClass) => {
  acc[processClass.encodedLabel] = processClass;
  return acc;
}, {} as Record<number, ProcessClass>);

const outages = computed(() => {
  if (!nonWorkingDaysByDaySet.value) {
    return outagesByRange.value;
  }

  return processDurationService.useCorrectOutages(
    outagesByRange.value,
    streams.value,
    nonWorkingDaysByDaySet.value,
    isWorkingDay.value,
  );
});

const processDetailMap = computed(() => {
  if (!projectDurationSettings.value || !outages.value) {
    return {};
  }

  return processDurationService.getProcessDetailMap(
    props.processes,
    projectDurationSettings.value,
    outages.value,
    props.interval && eachDayOfInterval({ start: props.interval[0], end: props.interval[1] }),
  );
});
const processDetailSummary = computed(() => {
  return processDurationService.getProcessDetailSummary(processDetailMap.value);
});

const totalDuration = computed(() => {
  return processDurationService.getTotalDuration(processDetailSummary.value);
});

const isWithinWorkingInterval = (day: string) => {
  return intervalDateStrings.value.has(day);
};

const isCurrentMonth = (currMonth: Date, date: string) => {
  return currMonth.getMonth() === new Date(date).getMonth();
};

const getBackgroundColor = (day: string, firstDayOfMonth: Date) => {
  if (!isCurrentMonth(firstDayOfMonth, day)) {
    return "";
  }

  const item = processDetailMap.value[day];

  if (item && item.working_day_count > 0) {
    if (item.status === "holiday") {
      return workingDaysColorCodes.holiday;
    }
    if (item.status === "critical_disturbance") {
      return workingDaysColorCodes.critical_disturbance;
    }
    if (item.non_critical_disturbances.length === 0) {
      if (item.status === "active") {
        return workingDaysColorCodes.active;
      } else if (item.status === "outage") {
        return workingDaysColorCodes.outage;
      } else {
        return workingDaysColorCodes.inactive;
      }
    }
    if (item.working_day_count > 0 && item.status) {
      return workingDaysColorCodes[item.status] || "";
    }
  }
  return "";
};

const calculateWorkHoursSum = (processes: ShortenedProcess[], decimalHours = false) => {
  return processDurationService.calculateWorkHoursSum(processes, decimalHours);
};

const getUniqueProcessElements = (processes: ShortenedProcess[]) => {
  const uniqueLabels: number[] = [
    ...new Set(processes.map((item: ShortenedProcess) => item?.encoded_label)),
  ];

  return [
    ...new Set(uniqueLabels.map((label) => getProcessClassFromEncodedLabel[label]?.processElement)),
  ];
};

const topVal = ref();
const leftVal = ref();

const handleMouseMoveOnDate = (event: MouseEvent, day: string) => {
  const target = event.target as HTMLElement | null;
  if (target) {
    const divLocation = target?.getBoundingClientRect();
    topVal.value = divLocation.y + 25;
    leftVal.value = divLocation.x + 10;
    tooltipDay.value = day;
  }
};

const currentPage = ref(1);
const totalPages = computed(() => {
  return Math.ceil(visibleMonthsStartDate.value.length / itemsPerPage);
});

const paginatedMonths = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return visibleMonthsStartDate.value.slice(startIndex, endIndex);
});

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const allCalendarDates = computed(() => {
  return paginatedMonths.value.map((monthStartDate) => {
    const startOfCurrentMonth = startOfMonth(monthStartDate);
    const endOfCurrentMonth = endOfMonth(monthStartDate);
    const start = startOfWeek(startOfCurrentMonth, { weekStartsOn: 1 });
    const end = endOfWeek(endOfCurrentMonth, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: startOfDay(start), end: endOfDay(end) }).map((day) =>
      format(day, "yyyy-MM-dd"),
    );
  });
});

const goToActivityLog = (date: string) => {
  if (processDetailMap.value[date].processes.length === 0) {
    return;
  }
  const processList = props.processes
    .filter((item) => item.date === date)
    .flatMap((item) => item._id);
  const link = {
    name: "ProcessesTable",
    query: {
      process_ids: processList.join(","),
    },
  };

  const routeData = router.resolve(link);

  if (routeData) {
    window.open(routeData.href, "_blank");
  }
};

const getCameraNameById = (id: string) => {
  const camera = streams.value.find((item) => item.camera_id === id);
  return camera ? camera.name : null;
};

return (_ctx: any,_cache: any) => {
  return (_ctx.processes)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass([
      'min-w-64',
      {
        'min-h-max lg:h-[700px] lg:w-[1000px] w-full': visibleMonthsStartDate.value.length > 8,
      },
    ])
      }, [
        (!_unref(isLoading))
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass([
        'flex flex-col justify-content-stretch justify-between ',
        { 'w-full h-full': visibleMonthsStartDate.value.length > 8 },
      ])
            }, [
              (visibleMonthsStartDate.value.length > 8)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "flex h-6 w-8 items-center justify-center rounded-l border-y border-x border-gray-300 pr-1 text-gray-400 focus:relative md:w-9 md:pr-0 md:hover:bg-yellow-100 disabled:text-gray-200 disabled:border-gray-200 hover:border-yellow hover:text-yellow disabled:hover:bg-transparent",
                      title: "Previous",
                      disabled: currentPage.value === 1,
                      onClick: prevPage
                    }, [
                      _hoisted_3,
                      _createVNode(_unref(ChevronLeftIcon), {
                        class: "h-4 w-4",
                        "aria-hidden": "true"
                      })
                    ], 8, _hoisted_2),
                    _createElementVNode("button", {
                      type: "button",
                      class: "flex h-6 w-8 items-center justify-center rounded-r border-y border-x border-gray-300 pl-1 text-gray-400 focus:relative md:w-9 md:pl-0 md:hover:bg-yellow-100 disabled:text-gray-200 disabled:border-gray-200 hover:border-yellow hover:text-yellow disabled:hover:bg-transparent",
                      title: "Next",
                      disabled: currentPage.value === totalPages.value,
                      onClick: nextPage
                    }, [
                      _hoisted_5,
                      _createVNode(_unref(ChevronRightIcon), {
                        class: "h-4 w-4",
                        "aria-hidden": "true"
                      })
                    ], 8, _hoisted_4)
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                class: _normalizeClass([
          'relative gap-x-8 gap-y-4 flex-1',
          visibleMonthsStartDate.value.length >= 4
            ? 'grid sm:grid-cols-2 lg:grid-cols-4 md:grid-cols-3'
            : 'flex flex-wrap',
        ])
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedMonths.value, (firstDayOfMonth, monthIdx) => {
                  return (_openBlock(), _createElementBlock("section", {
                    key: monthIdx,
                    class: _normalizeClass([
            visibleMonthsStartDate.value.length === 1 && 'sm:mx-auto',
            'mx-auto sm:mx-0 sm:w-auto w-full',
          ])
                  }, [
                    _createElementVNode("h2", _hoisted_6, _toDisplayString(_ctx.$t(`calendar.months.${_unref(format)(firstDayOfMonth, "M")}.long`)) + " " + _toDisplayString(_unref(format)(firstDayOfMonth, "yyyy")), 1),
                    _createElementVNode("div", _hoisted_7, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(7, (index) => {
                        return _createElementVNode("div", { key: index }, _toDisplayString(_ctx.$t(`calendar.week_days.${index}.short`).charAt(0)), 1)
                      }), 64))
                    ]),
                    _createElementVNode("div", {
                      ref_for: true,
                      ref_key: "calendar",
                      ref: calendar,
                      class: "mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-xs lg:text-sm shadow ring-1 ring-gray-200"
                    }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(allCalendarDates.value[monthIdx], (day, dayIdx) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: dayIdx,
                          class: _normalizeClass(["lg:p-1.5 p-0.5 focus:z-10 relative text-center", [
                dayIdx === 0 && 'rounded-tl-lg',
                dayIdx === 6 && 'rounded-tr-lg',
                processDetailMap.value[day]?.processes?.length > 0 && isCurrentMonth(firstDayOfMonth, day)
                  ? 'cursor-pointer'
                  : 'cursor-default',
                dayIdx === allCalendarDates.value[monthIdx].length - 7 && 'rounded-bl-lg',
                dayIdx === allCalendarDates.value[monthIdx].length - 1 && 'rounded-br-lg',
                tooltipDay.value === day && 'opacity-60',
                {
                  'bg-gray-400 text-white':
                    isWithinWorkingInterval(day) && isCurrentMonth(firstDayOfMonth, day),
                  'bg-white-500 hover:bg-gray-100':
                    (!isWithinWorkingInterval(day) && !processDetailMap.value[day]) ||
                    !isCurrentMonth(firstDayOfMonth, day),
                  [`diagonal-${processDetailMap.value[day]?.status}`]:
                    processDetailMap.value[day]?.working_day_count === 0.5 &&
                    isCurrentMonth(firstDayOfMonth, day),
                  startDate:
                    day === _unref(format)(intervalOrFallback.value[0], 'yyyy-MM-dd') &&
                    isCurrentMonth(firstDayOfMonth, day),
                  endDate:
                    day === _unref(format)(intervalOrFallback.value[1], 'yyyy-MM-dd') &&
                    isCurrentMonth(firstDayOfMonth, day),
                },
              ]]),
                          onClick: ($event: any) => (
                isWithinWorkingInterval(day) && isCurrentMonth(firstDayOfMonth, day)
                  ? goToActivityLog(day)
                  : ''
              ),
                          style: _normalizeStyle({
                backgroundColor: getBackgroundColor(day, firstDayOfMonth),
                color:
                  (!isWithinWorkingInterval(day) && !processDetailMap.value[day]) ||
                  !isCurrentMonth(firstDayOfMonth, day)
                    ? 'gray'
                    : 'white',
              })
                        }, [
                          (isCurrentMonth(firstDayOfMonth, day))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                (processDetailMap.value[day]?.non_critical_disturbances.length > 0)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_10))
                                  : _createCommentVNode("", true),
                                (
                    (processDetailMap.value[day]?.holidays.length > 0 ||
                      processDetailMap.value[day]?.critical_disturbances.length > 0) &&
                    processDetailMap.value[day].processes.length > 0
                  )
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_11))
                                  : _createCommentVNode("", true),
                                _createElementVNode("div", {
                                  class: _normalizeClass([
                    'group relative',
                    processDetailMap.value[day]?.processes?.length && isCurrentMonth(firstDayOfMonth, day)
                      ? 'cursor-pointer'
                      : 'cursor-default',
                  ]),
                                  onMouseover: ($event: any) => (handleMouseMoveOnDate($event, day)),
                                  onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (tooltipDay.value = ''))
                                }, [
                                  _createTextVNode(_toDisplayString(_unref(format)(new Date(day), "d")) + " ", 1),
                                  (tooltipDay.value === day)
                                    ? (_openBlock(), _createBlock(_Teleport, {
                                        key: 0,
                                        to: "#tooltip"
                                      }, [
                                        (isWithinWorkingInterval(day))
                                          ? (_openBlock(), _createElementBlock("div", {
                                              key: 0,
                                              style: _normalizeStyle({ top: topVal.value + 'px', left: leftVal.value + 'px' }),
                                              class: "tooltip-text tooltip-center h-max max-w-32 text-left flex-col gap-1.5 min-w-48 text-sm w-max fixed py-2 px-3 z-[9999] border rounded bg-white text-gray-600 flex"
                                            }, [
                                              _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(format)(new Date(day), "dd.MM.yyyy")) + ", " + _toDisplayString(_ctx.$t(`calendar.week_days.${_unref(getDay)(new Date(day))}.long`)), 1),
                                              _hoisted_14,
                                              (processDetailMap.value[day].working_day_count > 0)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                                                    _createElementVNode("span", {
                                                      class: "w-1.5 h-1.5 rounded-full",
                                                      style: _normalizeStyle({ backgroundColor: getBackgroundColor(day, firstDayOfMonth) })
                                                    }, null, 4),
                                                    _createElementVNode("small", null, _toDisplayString(_ctx.$t("analytics.reports.working_hours", 2)) + ": " + _toDisplayString(calculateWorkHoursSum(processDetailMap.value[day]?.processes, true)), 1)
                                                  ]))
                                                : _createCommentVNode("", true),
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getUniqueProcessElements(
                          processDetailMap.value[day]?.processes,
                        ), (processElement) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                  class: "flex mb-0.5 gap-y-0.5 gap-x-1 leading-none",
                                                  key: processElement
                                                }, [
                                                  _createElementVNode("div", {
                                                    class: "w-1",
                                                    style: _normalizeStyle(`background: ${_unref(processElementColor)[processElement as ProcessElement]}`)
                                                  }, null, 4),
                                                  _createElementVNode("small", null, _toDisplayString(_ctx.$t(`process_classes.${processElement}`)) + ": " + _toDisplayString(calculateWorkHoursSum(
                              processDetailMap.value[day]?.processes.filter((item) => {
                                return (
                                  _unref(getProcessClassFromEncodedLabel)[item?.encoded_label]
                                    .processElement === processElement
                                );
                              }),
                              true,
                            )) + " " + _toDisplayString(_ctx.$t("time.hour")), 1)
                                                ]))
                                              }), 128)),
                                              (processDetailMap.value[day].outages.length)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(processDetailMap.value[day].outages, (outage) => {
                                                      return (_openBlock(), _createElementBlock("div", {
                                                        key: outage.camera_id,
                                                        class: "grid grid-cols-2 gap-1 mt-1 text-xs items-center justify-between"
                                                      }, [
                                                        _createElementVNode("small", _hoisted_17, _toDisplayString(getCameraNameById(outage.camera_id)) + ":", 1),
                                                        _createElementVNode("small", {
                                                          style: _normalizeStyle({ backgroundColor: _unref(workingDaysColorCodes).outage }),
                                                          class: "rounded px-1 text-white py-0.5 col-span-1 text-center"
                                                        }, _toDisplayString(_unref(format)(outage.start_time, "HH:mm")) + " - " + _toDisplayString(_unref(format)(outage.end_time, "HH:mm")), 5)
                                                      ]))
                                                    }), 128))
                                                  ]))
                                                : _createCommentVNode("", true),
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(processDetailMap.value[day].holidays, (holiday, key) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                  key: key,
                                                  class: "overflow-hidden truncate whitespace-nowrap w-full"
                                                }, [
                                                  _createElementVNode("small", _hoisted_18, _toDisplayString(holiday), 1)
                                                ]))
                                              }), 128)),
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(processDetailMap.value[day]
                          ?.non_critical_disturbances, (critical_disturbance, index) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                  key: index,
                                                  class: "overflow-hidden truncate whitespace-nowrap w-full",
                                                  title: critical_disturbance
                                                }, [
                                                  _createElementVNode("small", _hoisted_20, _toDisplayString(critical_disturbance), 1)
                                                ], 8, _hoisted_19))
                                              }), 128)),
                                              (processDetailMap.value[day].working_day_count === 0)
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                                    _createElementVNode("small", _hoisted_22, _toDisplayString(_ctx.$t("working_day.non_working_day")), 1)
                                                  ]))
                                                : _createCommentVNode("", true)
                                            ], 4))
                                          : _createCommentVNode("", true)
                                      ]))
                                    : _createCommentVNode("", true)
                                ], 42, _hoisted_12)
                              ]))
                            : _createCommentVNode("", true)
                        ], 14, _hoisted_8))
                      }), 128))
                    ], 512)
                  ], 2))
                }), 128))
              ], 2),
              _createElementVNode("div", {
                class: _normalizeClass([
          'grid gap-1 mt-6 text-sm justify-center w-auto',
          visibleMonthsStartDate.value.length > 1 && 'sm:grid-cols-2 sm:justify-stretch',
        ])
              }, [
                _createElementVNode("div", null, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['active', 'inactive', 'outage'], (item) => {
                    return _createElementVNode("div", { key: item }, [
                      (processDetailSummary.value[item as keyof typeof processDetailSummary] !== 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                            _createElementVNode("span", {
                              style: _normalizeStyle({ backgroundColor: _unref(workingDaysColorCodes)[item] }),
                              class: "w-2.5 h-2.5 rounded-full"
                            }, null, 4),
                            _createElementVNode("small", _hoisted_24, [
                              _createTextVNode(_toDisplayString(_ctx.$t(`working_day.${item}`)) + ": ", 1),
                              _createElementVNode("span", null, _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary]), 1),
                              _createTextVNode(" " + _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary] > 1
                    ? _ctx.$t("working_day.working_day", 2)
                    : _ctx.$t("working_day.working_day", 1)), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  }), 64))
                ]),
                _createElementVNode("div", {
                  class: _normalizeClass(visibleMonthsStartDate.value.length > 1 && 'sm:justify-self-end')
                }, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['holiday', 'critical_disturbance'], (item) => {
                    return _createElementVNode("div", { key: item }, [
                      (processDetailSummary.value[item as keyof typeof processDetailSummary] !== 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                            _createElementVNode("div", {
                              style: _normalizeStyle({ backgroundColor: _unref(workingDaysColorCodes)[item] }),
                              class: "w-2.5 h-2.5 rounded-full"
                            }, null, 4),
                            _createElementVNode("small", _hoisted_26, [
                              _createTextVNode(_toDisplayString(_ctx.$t(`working_day.${item}`)) + ": ", 1),
                              _createElementVNode("span", null, _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary]), 1),
                              _createTextVNode(" " + _toDisplayString(processDetailSummary.value[item as keyof typeof processDetailSummary] > 1
                    ? _ctx.$t("working_day.working_day", 2)
                    : _ctx.$t("working_day.working_day", 1)), 1)
                            ])
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  }), 64))
                ], 2)
              ], 2),
              (totalDuration.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 1,
                    class: _normalizeClass([
          'text-sm mt-3 text-center sm:text-left',
          visibleMonthsStartDate.value.length === 1 && 'sm:text-center',
        ])
                  }, [
                    _createElementVNode("small", null, _toDisplayString(_ctx.$t("working_day.working_day", totalDuration.value)) + ": " + _toDisplayString(totalDuration.value), 1)
                  ], 2))
                : _createCommentVNode("", true)
            ], 2))
          : (_openBlock(), _createElementBlock("div", _hoisted_27, [
              _createVNode(LoadingSpinner, { size: "w-6 h-6" })
            ]))
      ], 2))
    : _createCommentVNode("", true)
}
}

})